'use client'
import { RefundProps, Reservation } from '@/root/shared-types'
import { useCustomRouter, usePrintedTicketData } from '@/root/shared-hooks'
import { ChangeEvent, useEffect, useState } from 'react'
import { reservationService } from '@/root/shared-api'
import useSWRMutation from 'swr/mutation'
import { getFormattedPercent } from '@/root/shared-hooks/refund/getFormattedPercent'
import { logger } from '@/logger'

let sailsQueue: Promise<unknown> = Promise.resolve()

const postRefundSails = ([, reservationId]: [string, number], { arg: sailRefIds }: { arg: number[] }) => {
  const request = sailsQueue
    .catch(() => undefined)
    .then(() => reservationService.refundReservationSails(reservationId, sailRefIds))
  sailsQueue = request
  return request
}

export const useRefund = (
  reservation: Reservation
): {
  isLoading: boolean
  refundProps?: RefundProps
  error?: unknown
} => {
  const ticketData = usePrintedTicketData(reservation)
  const {
    push,
    locale,
    query: { reservationToken },
  } = useCustomRouter()
  const ticket = ticketData.reservation
  const isPreselectedThere = Boolean(ticket?.there.isCancelled && !ticket.there.isRefund)
  const isPreselectedBack = Boolean(ticket?.back.isCancelled && !ticket.back.isRefund)
  const [isAgreementAccepted, setIsAgreementAccepted] = useState<boolean>(false)
  const [isSelectedRouteThere, setIsSelectedRouteThere] = useState<boolean>(isPreselectedThere)
  const [isSelectedRouteBack, setIsSelectedRouteBack] = useState<boolean>(isPreselectedBack)
  const [isCancellationInProgress, setIsCancellationInProgress] = useState(false)
  const [hasCancelError, setHasCancelError] = useState(false)

  const getSailRefIds = (there: boolean, back: boolean) =>
    ticket ? [...(there ? [ticket.there.sailRefId] : []), ...(back ? [ticket.back.sailRefId] : [])] : []

  const { data, error, isMutating, trigger } = useSWRMutation(
    ticket ? ['refund-sails', ticket.reservationId] : null,
    postRefundSails,
    { throwOnError: false }
  )

  // The offer for the starting selection (the page opens with amounts); later offers come from the checkboxes
  const [initialSailRefIds] = useState(() => getSailRefIds(isPreselectedThere, isPreselectedBack))
  const hasTicket = Boolean(ticket)
  useEffect(() => {
    if (hasTicket) void trigger(initialSailRefIds)
  }, [hasTicket, trigger, initialSailRefIds])

  if (!ticket) {
    return {
      isLoading: ticketData.isLoading,
      error: ticketData.error,
    }
  }

  if (error) {
    return {
      isLoading: isMutating,
      error,
    }
  }

  const { currency, amount, feeAmount, isRoundTrip, there, back, reservationId } = ticket

  const isAvailableCancelButton =
    isAgreementAccepted && (isRoundTrip ? isSelectedRouteThere || isSelectedRouteBack : true)
  const totalRefundAmount = data?.sails?.totalRefundAmount ?? 0
  const formattedPercent = getFormattedPercent({ totalRefundAmount, feeAmount, amount })

  const onRouteThereChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsSelectedRouteThere(event.target.checked)
    void trigger(getSailRefIds(event.target.checked, isSelectedRouteBack))
  }
  const onRouteBackChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsSelectedRouteBack(event.target.checked)
    void trigger(getSailRefIds(isSelectedRouteThere, event.target.checked))
  }

  const refundRouteInfoPropsThere = {
    departurePort: there.departurePort,
    arrivalPort: there.arrivalPort,
    arrival: there.arrival,
    departure: there.departure,
    isRoundTrip,
    checked: isSelectedRouteThere,
    disabled: there.isRefund || there.isCancelled || there.allowance?.cancel === false || isCancellationInProgress,
    isRefund: there.isRefund,
    isCancelled: there.isCancelled,
    onChange: onRouteThereChange,
  }
  const refundRouteInfoPropsBack = {
    departurePort: back.departurePort,
    arrivalPort: back.arrivalPort,
    arrival: back.arrival,
    departure: back.departure,
    isRoundTrip,
    checked: isSelectedRouteBack,
    disabled: back.isRefund || back.isCancelled || back.allowance?.cancel === false || isCancellationInProgress,
    isRefund: back.isRefund,
    isCancelled: back.isCancelled,
    onChange: onRouteBackChange,
  }
  const refundAmountInfoProps = {
    amount,
    totalRefundAmount,
    formattedPercent,
    currency,
    isFetching: isMutating,
  }
  const refundAgreementProps = {
    isAgreementAccepted,
    onAcceptAgreement: (event: ChangeEvent<HTMLInputElement>) => {
      setIsAgreementAccepted(event.target.checked)
    },
  }

  const actionButtonProps = {
    async onKeepTickets() {
      push(`/${locale}/ticket/${reservationToken}`)
    },
    async onCancel() {
      setIsCancellationInProgress(true)
      setHasCancelError(false)
      try {
        await reservationService.refundReservation(reservationId)
        // The flag stays set on success: Confirm remains disabled while the result page loads.
        push(`/${locale}/refund/result/${reservationToken}`)
      } catch (cancelError: unknown) {
        logger.error('Refund confirmation failed', { reservationId, error: cancelError })
        setHasCancelError(true)
        setIsCancellationInProgress(false)
      }
    },
    isAvailableCancelButton,
    isCancellationInProgress,
    // Not while any offer is still being calculated: Confirm accepts the last offer the backend created.
    canConfirm: isAvailableCancelButton && !isCancellationInProgress && !isMutating && data !== undefined,
    hasCancelError,
  }

  return {
    isLoading: false,
    refundProps: {
      refundRouteInfoPropsThere,
      refundRouteInfoPropsBack,
      refundAmountInfoProps,
      refundAgreementProps,
      actionButtonProps,
      reservationId,
      isRoundTrip,
    },
  }
}
